import { useImagePreload } from '@app/core/hooks/useImagePreload';
import { getThumbnailFrameCount } from '@app/core/thumbnails/utils';
import { WebAppFragmentThumbnailsDto } from '@mi-front/api-v2';
import { useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useInterval, useTimeoutFn } from 'react-use';

/** Hold time before starting the carousel (ignores accidental pointer passes). */
const HOVER_START_DELAY_MS = 800;

/** UI cadence for cycling sprite frames while hovering (not metadata.interval). */
const FRAME_INTERVAL_MS = 350;

/**
 * First sprite frame usually matches the card poster — start/cycle from the second frame.
 */
const FIRST_CAROUSEL_FRAME = 1;

/**
 * Hover state, sprite preload, and frame index for the fragment card thumbnail carousel.
 * Preload starts immediately on pointer enter; carousel only after a short settle delay.
 */
export function useFragmentCardThumbnailsHover(
  thumbnails: WebAppFragmentThumbnailsDto | undefined,
) {
  const [isPointerOver, setIsPointerOver] = useState(false);
  const [isHoverSettled, setIsHoverSettled] = useState(false);
  const [frameIndex, setFrameIndex] = useState(FIRST_CAROUSEL_FRAME);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const hasThumbnails = Boolean(thumbnails?.thumbnailUrl && thumbnails?.metadata);
  // Preload during the delay so the first frame is ready when the carousel arms.
  const preloadUrl = isPointerOver && hasThumbnails ? thumbnails?.thumbnailUrl : undefined;
  const isSpriteReady = useImagePreload(preloadUrl);

  const frameCount = thumbnails?.metadata ? getThumbnailFrameCount(thumbnails.metadata) : 1;

  const armCarousel = useCallback(() => setIsHoverSettled(true), []);
  const [, cancelSettle, startSettle] = useTimeoutFn(armCarousel, HOVER_START_DELAY_MS);

  // useTimeoutFn starts on mount; cancel so cards do not arm without hover.
  useEffect(() => {
    cancelSettle();
  }, [cancelSettle]);

  useEffect(() => {
    if (!isPointerOver || !hasThumbnails || prefersReducedMotion) {
      cancelSettle();
      setIsHoverSettled(false);
      return;
    }

    startSettle();
    return cancelSettle;
  }, [isPointerOver, hasThumbnails, prefersReducedMotion, cancelSettle, startSettle]);

  const shouldCycle = isHoverSettled && isSpriteReady && !prefersReducedMotion && frameCount > 1;

  useEffect(() => {
    if (shouldCycle) {
      setFrameIndex(FIRST_CAROUSEL_FRAME);
    }
  }, [shouldCycle]);

  useInterval(
    () => {
      setFrameIndex((prev) => {
        const next = prev + 1;
        return next >= frameCount ? FIRST_CAROUSEL_FRAME : next;
      });
    },
    shouldCycle ? FRAME_INTERVAL_MS : null,
  );

  function handleMouseEnter() {
    if (!hasThumbnails) {
      return;
    }
    setIsPointerOver(true);
  }

  function handleMouseLeave() {
    cancelSettle();
    setIsPointerOver(false);
    setIsHoverSettled(false);
    setFrameIndex(FIRST_CAROUSEL_FRAME);
  }

  return {
    frameIndex,
    showCarousel: isHoverSettled && isSpriteReady && hasThumbnails && !prefersReducedMotion,
    handleMouseEnter,
    handleMouseLeave,
  };
}
